import { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "concello360.admin.token";

const PROVINCIAS = ["A Coruña", "Lugo", "Ourense", "Pontevedra"];

const AdminGalicia = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [auth, setAuth] = useState(false);
  const [tokenInput, setTokenInput] = useState("");
  const [authError, setAuthError] = useState("");

  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [provincia, setProvincia] = useState("");
  const [onlyWithEmail, setOnlyWithEmail] = useState(true);
  const [copied, setCopied] = useState("");

  /* ----- auth ----- */
  const tryLogin = async (t) => {
    try {
      await axios.post(`${API}/admin/login`, { token: t });
      localStorage.setItem(TOKEN_KEY, t);
      setToken(t); setAuth(true); setAuthError("");
    } catch {
      setAuthError("Token incorrecto");
      localStorage.removeItem(TOKEN_KEY);
    }
  };
  useEffect(() => { if (token) tryLogin(token); /* eslint-disable-next-line */ }, []);

  /* ----- fetch ----- */
  const fetchData = useCallback(async () => {
    if (!auth) return;
    setLoading(true);
    try {
      const headers = { "X-Admin-Token": token };
      const params = new URLSearchParams();
      if (query.trim()) params.set("q", query.trim());
      if (provincia) params.set("provincia", provincia);
      if (onlyWithEmail) params.set("only_with_email", "true");

      const [d, s] = await Promise.all([
        axios.get(`${API}/admin/galicia?${params.toString()}`, { headers }),
        axios.get(`${API}/admin/galicia/stats`, { headers }),
      ]);
      setItems(d.data.items || []);
      setTotal(d.data.total || 0);
      setStats(s.data);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }, [auth, token, query, provincia, onlyWithEmail]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const exportCsv = () => {
    const url = `${API}/admin/galicia/export.csv`;
    fetch(url, { headers: { "X-Admin-Token": token } })
      .then(r => r.blob())
      .then(blob => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u;
        a.download = `concellos-galicia-${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(u);
      });
  };

  const copyAllEmails = async () => {
    const emails = items.flatMap(r => (r.emails_todos || "").split(";").filter(Boolean));
    const unique = [...new Set(emails)];
    await navigator.clipboard.writeText(unique.join(", "));
    setCopied(`${unique.length} emails copiados al portapapeles`);
    setTimeout(() => setCopied(""), 3500);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuth(false); setToken(""); setItems([]); setStats(null);
  };

  /* ----- UI auth ----- */
  if (!auth) {
    return (
      <div className="admin-login-wrap" data-testid="galicia-login">
        <div className="admin-login-card">
          <h2>Panel · Concellos de Galicia</h2>
          <p>Introduce el token de administración.</p>
          <input type="password" value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            placeholder="Token"
            onKeyDown={(e) => { if (e.key === "Enter") tryLogin(tokenInput); }}
            data-testid="galicia-token-input"
          />
          {authError && <div className="admin-error">{authError}</div>}
          <button className="btn" onClick={() => tryLogin(tokenInput)}
                  data-testid="galicia-login-btn">Entrar</button>
          <Link to="/" className="muted-link" style={{marginTop:14,display:"block",textAlign:"center"}}>← Volver a la landing</Link>
        </div>
      </div>
    );
  }

  /* ----- UI panel ----- */
  return (
    <div className="admin-wrap" data-testid="admin-galicia">
      <header className="admin-bar">
        <Link to="/" className="brand brand-light">
          <div className="logo">C</div>
          <div>Concello360<small>Concellos de Galicia · 313</small></div>
        </Link>
        <div className="admin-bar-actions">
          <Link to="/admin/leads" className="btn ghost">← Volver a Leads</Link>
          <button className="btn ghost" onClick={fetchData}>↻ Actualizar</button>
          <button className="btn" onClick={exportCsv} data-testid="galicia-export-csv">Exportar CSV</button>
          <button className="btn ghost" onClick={logout}>Salir</button>
        </div>
      </header>

      {stats && stats.available && (
        <>
        <section className="admin-stats">
          <div className="stat-card"><span className="n">{stats.total}</span><span className="l">Concellos</span></div>
          <div className="stat-card status-contacted"><span className="n">{stats.with_email}</span><span className="l">Con email</span></div>
          <div className="stat-card status-won"><span className="n">{stats.total_emails}</span><span className="l">Emails totales</span></div>
          {PROVINCIAS.map(p => (
            <div key={p}
                 className={`stat-card ${provincia === p ? "status-won" : ""}`}
                 style={{cursor: "pointer"}}
                 onClick={() => setProvincia(provincia === p ? "" : p)}
                 data-testid={`prov-${p}`}>
              <span className="n">{stats.by_provincia?.[p] || 0}</span>
              <span className="l">{p}{provincia === p ? " · activo" : ""}</span>
            </div>
          ))}
        </section>
        {stats.by_categoria && Object.keys(stats.by_categoria).length > 0 && (
          <section className="admin-stats" style={{marginTop:8,opacity:.92}}>
            {Object.entries(stats.by_categoria)
              .sort((a,b) => b[1] - a[1])
              .map(([k, n]) => (
                <div key={k} className="stat-card" style={{minWidth:140}}>
                  <span className="n" style={{fontSize:"1.4rem"}}>{n}</span>
                  <span className="l">{k.replace("email_", "").replace(/_/g, " ")}</span>
                </div>
            ))}
          </section>
        )}
        </>
      )}

      <section className="admin-filters">
        <input type="search"
          placeholder="Buscar por concello, provincia o alcalde…"
          value={query} onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") fetchData(); }}
          data-testid="galicia-search"
        />
        <label className="muted" style={{display:"flex",alignItems:"center",gap:8,whiteSpace:"nowrap"}}>
          <input type="checkbox" checked={onlyWithEmail}
                 onChange={(e) => setOnlyWithEmail(e.target.checked)}/> Sólo con email
        </label>
        <button className="btn ghost" onClick={copyAllEmails} data-testid="galicia-copy-emails">
          Copiar emails filtrados
        </button>
        {copied && <span style={{color:"#1f9d55", fontWeight:600}}>{copied}</span>}
      </section>

      {loading ? <div className="admin-empty">Cargando…</div> : (
        <div className="admin-table-wrap">
          <div style={{padding:"8px 18px",color:"#7c8d9c",fontSize:14}}>
            Mostrando <strong>{items.length}</strong> de {total} concellos
            {provincia && <> · provincia: <strong>{provincia}</strong></>}
          </div>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Provincia</th>
                <th>Concello</th>
                <th>Email principal</th>
                <th>Otros emails</th>
                <th>Teléfono</th>
                <th>Web</th>
                <th>Alcalde/sa</th>
                <th>Censo</th>
              </tr>
            </thead>
            <tbody>
              {items.map(r => {
                const others = (r.emails_todos || "").split(";").filter(e => e && e !== r.email_principal);
                return (
                  <tr key={r.concello + r.provincia}>
                    <td>{r.provincia}</td>
                    <td><strong>{r.concello}</strong></td>
                    <td>{r.email_principal
                      ? <a className="muted-link" href={`mailto:${r.email_principal}`}>{r.email_principal}</a>
                      : <span className="muted">—</span>}</td>
                    <td style={{maxWidth:260}}>
                      {others.length ? others.map(e => (
                        <a key={e} href={`mailto:${e}`} className="muted-link" style={{display:"block",fontSize:12}}>{e}</a>
                      )) : <span className="muted">—</span>}
                    </td>
                    <td className="nowrap">{r.telefono || <span className="muted">—</span>}</td>
                    <td style={{maxWidth:180,overflow:"hidden",textOverflow:"ellipsis"}}>
                      {r.web ? <a href={r.web} target="_blank" rel="noreferrer" className="muted-link">{r.web.replace(/^https?:\/\//,"")}</a> : <span className="muted">—</span>}
                    </td>
                    <td style={{maxWidth:180}}>{r.alcalde || <span className="muted">—</span>}</td>
                    <td className="nowrap">{r.censo || <span className="muted">—</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminGalicia;
